import { ProtocolError } from './errors';
import { PROTOCOL_VERSION } from './version';

export const PROTOCOL_MAGIC = 0x53425553;
export { PROTOCOL_VERSION } from './version';
export { ProtocolError } from './errors';
export const DEFAULT_CHUNK_SIZE = 32 * 1024;
export const DEFAULT_SYMBOL_SIZE = 512;
export const MAX_OBJECT_BYTES = 64 * 1024 * 1024;
export const MAX_GENERATION_SYMBOLS = 256;

export type TransferPhase =
  | 'IDLE'
  | 'PREPARE'
  | 'DISCOVER'
  | 'CALIBRATE'
  | 'KEY_CONFIRM'
  | 'TRANSFER'
  | 'REPAIR'
  | 'VERIFY'
  | 'COMPLETE'
  | 'FAILED';

export type PhysicalChannel = 'optical' | 'acoustic';
export type Direction = 'sender-to-receiver' | 'receiver-to-sender';
export type MetricSource = 'measured' | 'derived' | 'simulated' | 'unavailable';
export type IntegrityStage =
  | 'physical-crc'
  | 'fountain-rank'
  | 'chunk-hashes'
  | 'merkle-root'
  | 'aead-tags'
  | 'object-hash';

export type IntegrityState = 'pending' | 'active' | 'verified' | 'failed';

export interface ObjectDescriptor {
  readonly name: string;
  readonly mimeType: string;
  readonly byteLength: number;
}

export interface ChunkManifestEntry {
  readonly index: number;
  readonly plaintextLength: number;
  readonly ciphertextLength: number;
  readonly ciphertextHash: string;
}

export interface ObjectManifest extends ObjectDescriptor {
  readonly protocolVersion: string;
  readonly objectId: string;
  readonly chunkSize: number;
  readonly chunkCount: number;
  readonly chunks: readonly ChunkManifestEntry[];
  readonly objectHash: string;
  readonly merkleRoot: string;
  readonly compression: 'none';
}

export interface TransportPacket {
  readonly sessionTag: Uint8Array;
  readonly objectTag: Uint8Array;
  readonly direction: Direction;
  readonly channel: PhysicalChannel;
  readonly sequence: number;
  readonly generationId: number;
  readonly equationId: number;
  readonly sourceCount: number;
  readonly symbolSize: number;
  readonly validGenerationBytes: number;
  readonly payload: Uint8Array;
}

const allowedTransitions: Readonly<Record<TransferPhase, readonly TransferPhase[]>> = {
  IDLE: ['PREPARE'],
  PREPARE: ['DISCOVER', 'FAILED'],
  DISCOVER: ['CALIBRATE', 'FAILED'],
  CALIBRATE: ['KEY_CONFIRM', 'FAILED'],
  KEY_CONFIRM: ['TRANSFER', 'FAILED'],
  TRANSFER: ['REPAIR', 'VERIFY', 'FAILED'],
  REPAIR: ['VERIFY', 'FAILED'],
  VERIFY: ['COMPLETE', 'FAILED'],
  COMPLETE: ['IDLE'],
  FAILED: ['IDLE'],
};

export function assertPhaseTransition(from: TransferPhase, to: TransferPhase): void {
  if (!allowedTransitions[from].includes(to)) {
    throw new ProtocolError(
      'INVALID_PHASE_TRANSITION',
      `SpectraBus cannot transition from ${from} to ${to}.`,
    );
  }
}

export function concatBytes(...parts: readonly Uint8Array[]): Uint8Array {
  const length = parts.reduce((total, part) => total + part.length, 0);
  const output = new Uint8Array(length);
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }
  return output;
}

export function utf8(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

export function hexToBytes(value: string): Uint8Array {
  if (value.length % 2 !== 0 || !/^[\da-f]*$/iu.test(value)) {
    throw new ProtocolError('INVALID_HEX', 'Hexadecimal input must contain complete bytes.');
  }
  return Uint8Array.from(value.match(/.{2}/gu) ?? [], (byte) => Number.parseInt(byte, 16));
}

export function randomIdentifier(byteLength = 16): string {
  const bytes = new Uint8Array(byteLength);
  globalThis.crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
}

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < table.length; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) === 1 ? 0x82f63b78 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

export function crc32c(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc = (crcTable[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

const HEADER_BYTES = 50;
const CRC_BYTES = 4;
const CHANNEL_CODE: Readonly<Record<PhysicalChannel, number>> = {
  optical: 1,
  acoustic: 2,
};
const DIRECTION_CODE: Readonly<Record<Direction, number>> = {
  'sender-to-receiver': 1,
  'receiver-to-sender': 2,
};

function validatePacket(packet: TransportPacket): void {
  if (packet.sessionTag.length !== 8 || packet.objectTag.length !== 8) {
    throw new ProtocolError('INVALID_TAG_LENGTH', 'Session and object tags must be eight bytes.');
  }
  if (packet.payload.length !== packet.symbolSize) {
    throw new ProtocolError('INVALID_SYMBOL_SIZE', 'Payload length must equal the declared symbol size.');
  }
  if (
    packet.sourceCount < 1 ||
    packet.sourceCount > MAX_GENERATION_SYMBOLS ||
    packet.symbolSize < 1 ||
    packet.validGenerationBytes < 1 ||
    packet.validGenerationBytes > packet.sourceCount * packet.symbolSize
  ) {
    throw new ProtocolError('PACKET_BOUNDS', 'Transport packet exceeds negotiated generation bounds.');
  }
}

export function encodeTransportPacket(packet: TransportPacket): Uint8Array {
  validatePacket(packet);
  const output = new Uint8Array(HEADER_BYTES + packet.payload.length + CRC_BYTES);
  const view = new DataView(output.buffer);
  view.setUint32(0, PROTOCOL_MAGIC);
  view.setUint8(4, PROTOCOL_VERSION.major);
  view.setUint8(5, PROTOCOL_VERSION.minor);
  view.setUint8(6, CHANNEL_CODE[packet.channel]);
  view.setUint8(7, DIRECTION_CODE[packet.direction]);
  output.set(packet.sessionTag, 8);
  output.set(packet.objectTag, 16);
  view.setUint32(24, packet.sequence);
  view.setUint32(28, packet.generationId);
  view.setUint32(32, packet.equationId);
  view.setUint16(36, packet.sourceCount);
  view.setUint16(38, packet.symbolSize);
  view.setUint32(40, packet.validGenerationBytes);
  view.setUint16(44, packet.payload.length);
  view.setUint32(46, 0);
  output.set(packet.payload, HEADER_BYTES);
  view.setUint32(output.length - CRC_BYTES, crc32c(output.subarray(0, output.length - CRC_BYTES)));
  return output;
}

export function decodeTransportPacket(bytes: Uint8Array): TransportPacket {
  if (bytes.length < HEADER_BYTES + CRC_BYTES) {
    throw new ProtocolError('PACKET_TRUNCATED', 'Transport packet is shorter than its fixed header.');
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(0) !== PROTOCOL_MAGIC) {
    throw new ProtocolError('PACKET_MAGIC', 'Transport packet magic does not match SpectraBus.');
  }
  if (view.getUint8(4) !== PROTOCOL_VERSION.major) {
    throw new ProtocolError('PACKET_VERSION', 'Transport packet uses an unsupported major version.');
  }
  if (view.getUint32(46) !== 0) {
    throw new ProtocolError('PACKET_RESERVED', 'Transport packet contains non-zero reserved bytes.');
  }
  const payloadLength = view.getUint16(44);
  if (bytes.length !== HEADER_BYTES + payloadLength + CRC_BYTES) {
    throw new ProtocolError('PACKET_LENGTH', 'Transport packet length does not match its header.');
  }
  const expectedCrc = view.getUint32(bytes.length - CRC_BYTES);
  const actualCrc = crc32c(bytes.subarray(0, bytes.length - CRC_BYTES));
  if (actualCrc !== expectedCrc) {
    throw new ProtocolError('PHYSICAL_CRC', 'Physical-frame CRC32C verification failed.');
  }

  const channelCode = view.getUint8(6);
  const directionCode = view.getUint8(7);
  const channel = channelCode === 1 ? 'optical' : channelCode === 2 ? 'acoustic' : null;
  const direction =
    directionCode === 1
      ? 'sender-to-receiver'
      : directionCode === 2
        ? 'receiver-to-sender'
        : null;
  if (channel === null || direction === null) {
    throw new ProtocolError('PACKET_ENUM', 'Transport packet contains an unknown channel or direction.');
  }

  const packet: TransportPacket = {
    sessionTag: bytes.slice(8, 16),
    objectTag: bytes.slice(16, 24),
    direction,
    channel,
    sequence: view.getUint32(24),
    generationId: view.getUint32(28),
    equationId: view.getUint32(32),
    sourceCount: view.getUint16(36),
    symbolSize: view.getUint16(38),
    validGenerationBytes: view.getUint32(40),
    payload: bytes.slice(HEADER_BYTES, HEADER_BYTES + payloadLength),
  };
  validatePacket(packet);
  return packet;
}

export function equalBytes(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= (left[index] ?? 0) ^ (right[index] ?? 0);
  }
  return difference === 0;
}

export {
  SequenceReplayError,
  SequenceReplayWindow,
  SESSION_DISCOVERY_OFFER_BYTES,
  SESSION_KEY_CONFIRMATION_BYTES,
  SESSION_KEY_CONFIRMATION_TAG_BYTES,
  SESSION_KEY_RESPONSE_BYTES,
  canonicalPhysicalChannels,
  decodeKeyConfirmationMessage,
  decodeReceiverKeyResponse,
  decodeSenderDiscoveryOffer,
  encodeKeyConfirmationContext,
  encodeKeyConfirmationMessage,
  encodeReceiverKeyResponse,
  encodeSenderDiscoveryOffer,
  type KeyConfirmationContext,
  type KeyConfirmationMessage,
  type ReceiverKeyResponse,
  type SenderDiscoveryOffer,
  type SequenceReplayFailure,
} from './session-handshake';
